/*======================================================================================================================
  * Bluebell: src/app/state/mappings/mappings.reducer.ts
  * Last Edited: 11/03/2025
  * Copyright 2025 Bramble CFD LTD
  * The contents of this file are NOT for redistribution
  * See AUTHORS for list of developers on this project
 =====================================================================================================================*/
import { createEntityAdapter, EntityState, Update } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import { programmeProjectSelectionUnapplied } from '@state/account/account.actions';
import { MappingActions } from '@state/mappings/mappings.actions';
import { Mapping } from '@state/mappings/mappings.interfaces';


export interface State extends EntityState<Mapping> {
  loading: boolean;
  lastCreated: string|null;
}

const adapter = createEntityAdapter<Mapping>({
  selectId: (mapping: Mapping) => mapping.uuid,
});

export const initialState: State = adapter.getInitialState<State>({
  loading: false,
  lastCreated: null,
});

export const reducer = createReducer(
  initialState,

  on(programmeProjectSelectionUnapplied, () => {
    return adapter.removeAll({ ...initialState });
  }),

  on(MappingActions.retrieve, (state) => {
    return {
      ...state,
      loading: true,
    };
  }),

  on(MappingActions.retrieveSuccess, (state, { mappings }) => {
    return adapter.setAll(
      mappings,
      {
        ...state,
        loading: false,
      }
    );
  }),

  on(MappingActions.retrieveFailure, (state) => {
    return adapter.removeAll({
      ...state,
      loading: false,
    });
  }),

  on(MappingActions.createSuccess, (state, { mapping }) => {
    return adapter.upsertOne(
      mapping,
      {
        ...state,
        lastCreated: mapping.uuid,
      }
    );
  }),

  on(MappingActions.renameSuccess, (state, { mapping }) => {
    const update: Update<Mapping> = {
      id: mapping.uuid,
      changes: { name: mapping.name },
    };
    return adapter.updateOne(update, state);
  }),

  on(MappingActions.deleteSuccess, ( state, { uuid }) => {
    return adapter.removeOne(uuid, state);
  }),

  on(MappingActions.generateParetoDataSuccess, (state, { uuid, pareto }) => {
    const update: Update<Mapping> = {
      id: uuid,
      changes: { pareto },
    };
    return adapter.updateOne(update, state);
  }),

  on(MappingActions.generateCloudDataSuccess, (state, { uuid, cloud }) => {
    const update: Update<Mapping> = {
      id: uuid,
      changes: { cloud },
    };
    return adapter.updateOne(update, state);
  }),



);


export const {
  selectIds: getIds,
  selectEntities: getEntities,
  selectAll: getAll,
  selectTotal: getTotal,
} = adapter.getSelectors();

export const featureKey = 'mappings';
export const getLoading = (state: State) => state.loading;
export const getLastCreated = (state: State) => state.lastCreated;
